import { useRef, useState, type PointerEvent } from "react";
import type { Visual } from "../../../shared/math/types";
import { BallPiece } from "../BallPieces";
import "./story.css";
export const isStoryPuzzle = (kind: string) =>
  ["sum-path", "number-bond", "balance"].includes(kind);
function Pieces({ count, layout = 0 }: { count: number; layout?: number }) {
  return (
    <div
      className={`story-pieces arrangement-${layout}`}
      aria-label={`${count} things`}
    >
      {Array.from({ length: count }, (_, i) => (
        <span key={i} aria-hidden="true">
          <BallPiece />
        </span>
      ))}
      {!count && <span className="story-empty">0 · None</span>}
    </div>
  );
}
export function YoungPuzzleModel({
  visual,
  value = "",
  onChange,
  disabled = false,
}: {
  visual: Visual;
  value?: string;
  onChange?: (s: string) => void;
  disabled?: boolean;
}) {
  const [ghost, setGhost] = useState<{ x: number; y: number } | null>(null),
    well = useRef<HTMLDivElement>(null),
    drag = useRef<{ x: number; y: number; moved: boolean } | null>(null),
    blockClick = useRef(false);
  const v = visual.values,
    n = Math.min(20, Math.max(0, Number(value) || 0)),
    known = v[1] ?? 0,
    layout = visual.kind === "balance" ? (v[3] ?? 0) : (v[2] ?? 0);
  const answer = (
    <div
      className={`story-answer-well ${ghost ? "drag-ready" : ""}`}
      ref={well}
      aria-label="Your mystery team"
      data-answer-well="true"
    >
      <strong>{value === "" ? "?" : value}</strong>
      <small>Your team</small>
      {value !== "" &&
        (Number(value) <= 20 ? (
          <Pieces count={n} layout={0} />
        ) : (
          <small>More than 20 things</small>
        ))}
    </div>
  );
  function move(e: PointerEvent<HTMLButtonElement>) {
    if (!drag.current) return;
    if (
      Math.abs(e.clientX - drag.current.x) +
        Math.abs(e.clientY - drag.current.y) >
      8
    ) {
      drag.current.moved = true;
      setGhost({ x: e.clientX, y: e.clientY });
    }
  }
  function end(e: PointerEvent<HTMLButtonElement>) {
    const moved = drag.current?.moved;
    drag.current = null;
    setGhost(null);
    if (moved) {
      blockClick.current = true;
      const r = well.current?.getBoundingClientRect();
      if (
        r &&
        e.clientX >= r.left &&
        e.clientX <= r.right &&
        e.clientY >= r.top &&
        e.clientY <= r.bottom &&
        n < 20
      )
        onChange?.(String(n + 1));
    }
    if (e.currentTarget.hasPointerCapture(e.pointerId))
      e.currentTarget.releasePointerCapture(e.pointerId);
  }
  let model;
  if (visual.kind === "sum-path" && v[3] === 1)
    model = (
      <div className="story-symbol-path">
        <svg
          viewBox="0 0 500 180"
          role="img"
          aria-label={`Start at ${Math.max(v[0]??0,v[1]??0)}, add ${Math.min(v[0]??0,v[1]??0)}, find where the path ends`}
        >
          <path
            d={`M60 125Q135 ${25 + layout * 8} 225 50T440 125`}
            fill="none"
            stroke="#c8d7b5"
            strokeWidth="24"
            strokeLinecap="round"
          />
          <circle
            cx="60"
            cy="125"
            r="34"
            fill="#edf2df"
            stroke="#4e7159"
            strokeWidth="3"
          />
          <circle
            cx="225"
            cy="50"
            r="35"
            fill="#f6dda2"
            stroke="#9e803d"
            strokeWidth="3"
          />
          <circle
            cx="440"
            cy="125"
            r="34"
            fill="#eee5ed"
            stroke="#906d88"
            strokeWidth="3"
          />
          <text
            data-given="first"
            x="60"
            y="135"
            textAnchor="middle"
            fontSize="30"
            fill="#243b45"
          >
            {Math.max(v[0]??0,v[1]??0)}
          </text>
          <text
            data-given="second"
            x="225"
            y="60"
            textAnchor="middle"
            fontSize="30"
            fill="#243b45"
          >
            +{Math.min(v[0]??0,v[1]??0)}
          </text>
          <text
            x="440"
            y="135"
            textAnchor="middle"
            fontSize="30"
            fill="#243b45"
          >
            ?
          </text>
        </svg>
        <div className="story-path-labels">
          <span>Start</span>
          <span>Add this team</span>
          <span>Whole</span>
        </div>
        {answer}
      </div>
    );
  else if (visual.kind === "sum-path")
    model = (
      <div className="story-sum-path">
        <div className="story-team">
          <span>Milo’s team</span>
          <strong data-given="first">{v[0]}</strong>
          <Pieces count={v[0] ?? 0} layout={layout} />
        </div>
        <span className="story-operator" aria-hidden="true">
          +
        </span>
        <div className="story-team">
          <span>Junie’s team</span>
          <strong data-given="second">{known}</strong>
          <Pieces count={known} layout={(layout + 1) % 4} />
        </div>
        <span className="story-operator" aria-hidden="true">
          →
        </span>
        {answer}
      </div>
    );
  else if (visual.kind === "number-bond")
    model = (
      <div className="story-number-bond">
        <div className="story-whole">
          <span>Whole team</span>
          <strong data-given="whole">{v[0]}</strong>
          <Pieces count={v[0] ?? 0} layout={layout} />
        </div>
        <svg viewBox="0 0 400 65" aria-hidden="true">
          <path
            d="M200 0L95 60 M200 0L305 60"
            stroke="#315e51"
            strokeWidth="6"
            fill="none"
          />
        </svg>
        <div className="story-bond-parts">
          <div className="story-team">
            <span>One part</span>
            <strong data-given="known">{known}</strong>
            <Pieces count={known} layout={(layout + 1) % 4} />
          </div>
          {answer}
        </div>
      </div>
    );
  else {
    const left = v[0] ?? 0,
      right = (v[1] ?? 0) + (v[2] ?? 0),
      angle =
        value === "" ? 0 : Math.max(-9, Math.min(9, (left + n - right) * 2));
    model = (
      <div className="story-balance">
        <svg
          viewBox="0 0 500 115"
          role="img"
          aria-label="Balance beam; matching counts make it level"
        >
          <path d="M250 25V110 M215 110H285" stroke="#315e51" strokeWidth="9" />
          <g transform={`rotate(${-angle} 250 35)`}>
            <path
              d="M60 35H440"
              stroke="#926745"
              strokeWidth="12"
              strokeLinecap="round"
            />
            <path d="M75 35V82 M425 35V82" stroke="#315e51" strokeWidth="4" />
            <path
              d="M35 82Q75 118 115 82 M385 82Q425 118 465 82"
              fill="#f7d778"
              stroke="#926745"
              strokeWidth="4"
            />
          </g>
        </svg>
        <div className="story-balance-teams">
          <div className="story-left-team">
            <div className="story-team">
              <span>Left team</span>
              <strong data-given="left">{left}</strong>
              <Pieces count={left} layout={layout} />
            </div>
            <span aria-hidden="true">+</span>
            {answer}
          </div>
          <div className="story-team">
            <span>Right team</span>
            <strong data-given="right-first">{known}</strong>
            <Pieces count={known} layout={layout} />
            {(v[4] ?? 0) > 0 && (
              <>
                <span aria-hidden="true">+</span>
                <strong data-given="right-second">{v[2]}</strong>
                <Pieces count={v[2] ?? 0} layout={(layout + 1) % 4} />
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="story-puzzle-model"
      role="group"
      aria-label={visual.label}
      data-puzzle-kind={visual.kind}
    >
      {model}
      {onChange && (
        <div className="story-piece-tray">
          {visual.kind==='sum-path'&&<button type="button" disabled={disabled} onClick={()=>onChange(String(Math.max(v[0]??0,v[1]??0)))}>Start at {Math.max(v[0]??0,v[1]??0)} · count on {Math.min(v[0]??0,v[1]??0)}</button>}
          <button
            type="button"
            className="story-drag-piece"
            disabled={disabled || n >= 20}
            aria-label="Add one treasure, or drag it to your team"
            onPointerDown={(e) => {
              if (disabled) return;
              blockClick.current = false;
              drag.current = { x: e.clientX, y: e.clientY, moved: false };
              e.currentTarget.setPointerCapture(e.pointerId);
            }}
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={() => {
              drag.current = null;
              setGhost(null);
            }}
            onClick={() => {
              if (blockClick.current) {
                blockClick.current = false;
                return;
              }
              onChange(String(n + 1));
            }}
          >
            <BallPiece />
            <span>Drag or tap +1</span>
          </button>
          <button
            type="button"
            disabled={disabled || n === 0}
            onClick={() => onChange(String(n - 1))}
            aria-label="Remove one treasure"
          >
            −1
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange("0")}
          >
            None · 0
          </button>
        </div>
      )}
      {ghost && (
        <div
          className="story-drag-ghost"
          style={{ left: ghost.x, top: ghost.y }}
          aria-hidden="true"
        >
          <BallPiece />
        </div>
      )}
    </div>
  );
}
